import axios, {
  InternalAxiosRequestConfig,
  AxiosError,
  AxiosResponse,
} from "axios";
import { deleteCookie, setCookie } from "cookies-next";
import { IApiError } from "@/interface/apiError.types";
import { COOKIE_CONFIG } from "@/config";
import { showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";
import { clearProjectCreationStore } from "@/lib/store-utils/store.util";

const baseURL = process.env.NEXT_PUBLIC_API_URL;

// Validate API URL
const validateApiUrl = () => {
  if (!baseURL) {
    throw new Error("NEXT_PUBLIC_API_URL is not configured");
  }

  try {
    const url = new URL(baseURL);
    if (!["http:", "https:"].includes(url.protocol)) {
      throw new Error("Invalid API URL protocol");
    }
    return baseURL;
  } catch {
    throw new Error("Invalid NEXT_PUBLIC_API_URL format");
  }
};

const axiosInstance = axios.create({
  baseURL: validateApiUrl(),
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

const refreshToken = async () => {
  try {
    await axiosInstance.post(`/refresh`);
    setCookie(COOKIE_CONFIG.loggedIn, "true", {
      maxAge: 30 * 24 * 60 * 60,
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
    });
    return;
  } catch (error) {
    console.error("Refresh token failed", error);
    throw error;
  }
};

const logoutFlow = async (error: IApiError) => {
  // Logout user on 401 Unauthorized response
  localStorage.clear();
  clearProjectCreationStore();
  deleteCookie(COOKIE_CONFIG.loggedIn);
  deleteCookie(COOKIE_CONFIG.profile);
  deleteCookie(COOKIE_CONFIG.version);
  const useProfileStore = (await import("@/store/useProfileStore")).default;
  useProfileStore.getState().clearProfile();
  showToast(
    TOAST_TYPES.error,
    error?.response?.data?.message || "Unauthorized access"
  );
  window.location.href = "/login";
};

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    // Handle request errors here
    return Promise.reject(
      error instanceof Error ? error : new Error(String(error))
    );
  }
);

let isRefreshing = false;
const requestQueue: any[] = [];

// Helper functions to reduce cognitive complexity
const createStandardError = (error: unknown): Error => {
  return error instanceof Error ? error : new Error(String(error));
};

const validateResponseStructure = (response: AxiosResponse) => {
  if (!response?.data || typeof response.data !== "object") {
    const error = new Error(
      "Invalid response format: Expected an object response"
    );
    Object.assign(error, {
      response: {
        status: 400,
        data: {
          message: "Invalid response format: Expected an object response",
          error: "Bad Response Format",
        },
      },
    });
    throw error;
  }
  return response;
};

const queueRequest = (config: InternalAxiosRequestConfig) => {
  return new Promise((resolve) => {
    requestQueue.push(() => {
      resolve(axiosInstance(config));
    });
  });
};

const processQueuedRequests = () => {
  requestQueue.forEach((req) => req());
  requestQueue.length = 0;
};

const handleRetryAfterRefresh = async (
  originalRequest: InternalAxiosRequestConfig
) => {
  try {
    const response = await axiosInstance(originalRequest);
    processQueuedRequests();
    return response;
  } catch (error: unknown) {
    const errorResponse = error as IApiError;
    if (errorResponse?.response?.status === 401) {
      await logoutFlow(errorResponse);
    }
    return Promise.reject(createStandardError(error));
  }
};

const handleRefreshTokenFlow = async (error: AxiosError) => {
  try {
    await refreshToken();
    const originalRequest = error.response?.config;
    isRefreshing = false;

    if (!originalRequest) {
      throw new Error("Original request config not found");
    }

    return handleRetryAfterRefresh(originalRequest);
  } catch (refreshError: unknown) {
    const errorResponse = refreshError as IApiError;
    await logoutFlow(errorResponse);
    return Promise.reject(createStandardError(refreshError));
  }
};

const handleUnauthorizedError = async (error: AxiosError) => {
  // Only proceed with logout flow if we're not already on the login page
  if (window.location.pathname === "/login") {
    return Promise.reject(createStandardError(error));
  }

  // If a refresh is already in progress, queue the request
  if (isRefreshing) {
    if (!error.config) {
      return Promise.reject(createStandardError(error));
    }
    return queueRequest(error.config);
  }

  // Set the flag to indicate a token refresh is in progress
  isRefreshing = true;
  return handleRefreshTokenFlow(error);
};

const handleForbiddenError = async (error: AxiosError) => {
  // Only proceed with logout flow if we're not already on the login page
  if (window.location.pathname === "/login") {
    return Promise.reject(createStandardError(error));
  }

  // Handle 403 Forbidden - user doesn't have permission
  await logoutFlow(error as IApiError);
  
  return Promise.reject(createStandardError(error));
};

const handleNotFoundError = async (error: AxiosError) => {
  // Handle 404 Not Found - extract custom message from server response
  const responseData = error?.response?.data as any;
  const customMessage = responseData?.message || responseData?.error || "Resource not found";
  const customError = new Error(customMessage);
  Object.assign(customError, { response: error.response });
  return Promise.reject(customError);
};

// Response interceptor
axiosInstance.interceptors.response.use(
  async (response) => {
    // You can modify the response data here
    // Validate response structure
    return validateResponseStructure(response);
  },
  async (error) => {
    // Handle response errors here
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response?.status === 401) {
        return handleUnauthorizedError(error);
      }
      
      if (error.response?.status === 403) {
        return handleForbiddenError(error);
      }

      if (error.response?.status === 404) {
        return handleNotFoundError(error);
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error("Request error:", error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error("Error:", error.message);
    }
    return Promise.reject(createStandardError(error));
  }
);

export default axiosInstance;
